import css from './style.module.css';
import { Field, Form, Formik } from 'formik';
import * as Yup from 'yup';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';

export default function AddNewClient({ loginToken, setAddClient, addClient, setCompaniesData }) {
  
  const navigation = useNavigate();
  const [error, setError] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileError, setFileError] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStage, setUploadStage] = useState('');

  // Separate visibility state for each field
  const [passwordVisibility, setPasswordVisibility] = useState({
    sfUsername: false,
    sfPassword: false,
    sfSecurityToken: false,
    oktaClientID: false
  });

  const validationSchema = Yup.object({
    companyName: Yup.string()
      .required('Company name is required')
      .min(2, 'Company name must be at least 2 characters'),
    oktaDomain: Yup.string()
      .url('Must be a valid URL')
      .nullable(),
    oktaClientID: Yup.string()
      .min(10, 'Client ID must be at least 10 characters')
      .nullable(),
    sfUsername: Yup.string()
      .required('Salesforce username is required')
      .email('Must be a valid email address'),
    sfSecurityToken: Yup.string()
      .required('Security token is required'),
    companyDomain: Yup.string()
      .required('Company domain is required')
      .matches(
        /^[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9]?(\.[a-zA-Z]{2,})+$/,
        'Must be a valid domain (e.g., pirm.io)'
      ),
    sfPassword: Yup.string()
      .required('Salesforce password is required'),
    senderEmail: Yup.string()
      .email('Must be a valid email address')
      .required('Sender email is required'),
    status: Yup.string()
      .required('Status is required')
      .oneOf(['active', 'inactive'], 'Invalid status selection')
  });

  // Toggle visibility for specific field
  const toggleVisibility = (fieldName) => {
    setPasswordVisibility(prev => ({
      ...prev,
      [fieldName]: !prev[fieldName]
    }));
  };

  // Handle file selection with WEBP validation
  const handleFileChange = (event) => {
    const file = event.target.files[0];

    if (file) {
      // Check if file is WEBP
      if (file.type !== 'image/webp') {
        setFileError('Only WEBP image files are allowed');
        setSelectedFile(null);
        event.target.value = ''; // Clear the input
        return;
      }

      // Optional: Check file size (e.g., max 5MB)
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        setFileError('File size must be less than 5MB');
        setSelectedFile(null);
        event.target.value = '';
        return;
      }

      setSelectedFile(file);
      setFileError('');
      setError('');
    }
  };

  // Upload file to GCS
  const uploadFileToGCS = async (file, companyName) => {
    try {
      setUploadStage("Getting upload permission...");
      setUploadProgress(10);

      // Step 1: Get signed URL from backend
      const signedUrlResponse = await axios.post(
        `${process.env.REACT_APP_API_URL}/signed-url`,
        {
          fileName: companyName.replace(/ /g, ""),
          contentType: file.type,
          company: true
        },
        {
          headers: {
            Authorization: `Bearer ${loginToken}`,
          },
        }
      );

      const { signedUrl, publicUrl, fileName } = signedUrlResponse.data;
      // console.log({ signedUrl, publicUrl, fileName });

      // Step 2: Upload file directly to GCS
      setUploadStage("Uploading file...");
      setUploadProgress(15);

      const xhr = new XMLHttpRequest();

      const uploadPromise = new Promise((resolve, reject) => {
        xhr.upload.addEventListener('progress', (event) => {
          if (event.lengthComputable) {
            const fileUploadProgress = Math.round((event.loaded / event.total) * 70);
            setUploadProgress(15 + fileUploadProgress);
          }
        });

        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            setUploadProgress(90);
            setUploadStage("File uploaded successfully...");
            resolve({ publicUrl, fileName });
          } else {
            reject(new Error(`Upload failed with status: ${xhr.status}`));
          }
        });

        xhr.addEventListener('error', () => {
          reject(new Error('Upload failed'));
        });

        xhr.open('PUT', signedUrl);
        xhr.setRequestHeader('Content-Type', file.type);
        xhr.send(file);
      });

      return await uploadPromise;
    } catch (error) {
      console.error("Upload error:", error);
      throw error;
    }
  };

  const submitNewClient = async (values, { setSubmitting, resetForm }) => {
    setError('');

    if (!selectedFile) {
      setFileError('Please select a WEBP image file to upload');
      setSubmitting(false);
      return;
    }

    try {
      // Upload file to GCS first
      setUploadStage("Starting upload...");
      const { publicUrl } = await uploadFileToGCS(selectedFile, values.companyName);

      // Step 3: Submit form data with file URL
      setUploadStage("Processing client data...");
      setUploadProgress(95);

      const payload = {
        ...values,
        file: publicUrl,
      };

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/companies/add`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${loginToken}`,
          },
        }
      );

      if (response.status !== 200) {
        throw new Error('Failed to create client');
      }

      setCompaniesData(prevData => [...prevData, response.data.companyInfo]);
      setUploadProgress(100);
      setUploadStage("Client created successfully!");

      // Success - reset and close modal after brief delay
      setTimeout(() => {
        setUploadProgress(0);
        setUploadStage('');
        setSelectedFile(null);
        setFileError('');
        resetForm();
        setAddClient(false);
      }, 1500);

    } catch (error) {
      if (error?.response?.status === 401) {
        localStorage.removeItem("Firebase Auth");
        localStorage.removeItem("okta-token-storage");
        localStorage.removeItem("okta-cache-storage");
        navigation('/');
        return;
      } else {
        const errorMessage = error.response?.data?.message || 'Failed to create client';
        setError(errorMessage);
      }
      setUploadProgress(0);
      setUploadStage('');
    } finally {
      setSubmitting(false);
    }
  };

  // Error message component for consistent styling
  const ErrorMessage = ({ message }) => (
    <div className="text-danger text-start ms-1 mt-1" style={{ fontSize: '0.875rem' }}>
      <i className="fa-solid fa-exclamation-circle me-1"></i>
      {message}
    </div>
  );

  return (
    <section className={css.addNewClientSection}>
      <div className={css.addNewClientContainer}>
        <div className='d-flex justify-content-between align-items-center mb-3'>
          <h4>Add New Client</h4>
          <i
            className={`fa-regular fa-circle-xmark fa-xl holdCursor ${css.closeIcon}`}
            onClick={() => setAddClient(false)}
          ></i>
        </div>

        <Formik
          initialValues={{
            companyName: '',
            oktaDomain: '',
            oktaClientID: '',
            sfUsername: '',
            sfSecurityToken: '',
            companyDomain: '',
            sfPassword: '',
            senderEmail: '',
            status: ''
          }}
          validationSchema={validationSchema}
          onSubmit={submitNewClient}
        >
          {({ errors, touched, isSubmitting }) => (
            <Form>
              <div className='row'>
                <div className="col-md-6">
                  {/* Company Name */}
                  <div className="mb-3">
                    <label htmlFor="companyName" className="d-flex justify-start my-1 ms-3">
                      Company Name <span className="text-danger ms-1">*</span>
                    </label>
                    <Field
                      type='text'
                      name="companyName"
                      placeholder="Company Name"
                      className={`${css.searchInput} inputStyle ${errors.companyName && touched.companyName ? 'border-danger' : ''}`}
                    />
                    {errors.companyName && touched.companyName && (
                      <ErrorMessage message={errors.companyName} />
                    )}
                  </div>

                  {/* Salesforce Username */}
                  <div className="mb-3">
                    <label htmlFor="sfUsername" className="d-flex justify-start my-1 ms-3">
                      Salesforce Username <span className="text-danger ms-1">*</span>
                    </label>
                    <div className="position-relative">
                      <Field
                        type={passwordVisibility.sfUsername ? 'text' : 'password'}
                        name="sfUsername"
                        placeholder="Salesforce Username"
                        className={`${css.searchInput} inputStyle ${errors.sfUsername && touched.sfUsername ? 'border-danger' : ''}`}
                        style={{ paddingRight: '40px' }}
                      />
                      <div
                        className="position-absolute end-0 pe-3"
                        style={{
                          cursor: 'pointer',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          zIndex: 10
                        }}
                        onClick={() => toggleVisibility('sfUsername')}
                      >
                        <i className={`fa-solid ${passwordVisibility.sfUsername ? 'fa-eye-slash' : 'fa-eye'}`} />
                      </div>
                    </div>
                    {errors.sfUsername && touched.sfUsername && (
                      <ErrorMessage message={errors.sfUsername} />
                    )}
                  </div>

                  {/* Salesforce Password */}
                  <div className="mb-3">
                    <label htmlFor="sfPassword" className="d-flex justify-start my-1 ms-3">
                      Salesforce Password <span className="text-danger ms-1">*</span>
                    </label>
                    <div className="position-relative">
                      <Field
                        type={passwordVisibility.sfPassword ? 'text' : 'password'}
                        name="sfPassword"
                        placeholder="Salesforce Password"
                        className={`${css.searchInput} inputStyle ${errors.sfPassword && touched.sfPassword ? 'border-danger' : ''}`}
                        style={{ paddingRight: '40px' }}
                      />
                      <div
                        className="position-absolute end-0 pe-3"
                        style={{
                          cursor: 'pointer',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          zIndex: 10
                        }}
                        onClick={() => toggleVisibility('sfPassword')}
                      >
                        <i className={`fa-solid ${passwordVisibility.sfPassword ? 'fa-eye-slash' : 'fa-eye'}`} />
                      </div>
                    </div>
                    {errors.sfPassword && touched.sfPassword && (
                      <ErrorMessage message={errors.sfPassword} />
                    )}
                  </div>

                  {/* Salesforce Security Token */}
                  <div className="mb-3">
                    <label htmlFor="sfSecurityToken" className="d-flex justify-start my-1 ms-3">
                      Salesforce Token <span className="text-danger ms-1">*</span>
                    </label>
                    <div className="position-relative">
                      <Field
                        type={passwordVisibility.sfSecurityToken ? 'text' : 'password'}
                        name="sfSecurityToken"
                        placeholder="Salesforce Token"
                        className={`${css.searchInput} inputStyle ${errors.sfSecurityToken && touched.sfSecurityToken ? 'border-danger' : ''}`}
                        style={{ paddingRight: '40px' }}
                      />
                      <div
                        className="position-absolute end-0 pe-3"
                        style={{
                          cursor: 'pointer',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          zIndex: 10
                        }}
                        onClick={() => toggleVisibility('sfSecurityToken')}
                      >
                        <i className={`fa-solid ${passwordVisibility.sfSecurityToken ? 'fa-eye-slash' : 'fa-eye'}`} />
                      </div>
                    </div>
                    {errors.sfSecurityToken && touched.sfSecurityToken && (
                      <ErrorMessage message={errors.sfSecurityToken} />
                    )}
                  </div>

                  {/* Company Domain */}
                  <div className="mb-3">
                    <label htmlFor="companyDomain" className="d-flex justify-start my-1 ms-3">
                      Company Domain <span className="text-danger ms-1">*</span>
                    </label>
                    <Field
                      type='text'
                      name="companyDomain"
                      placeholder="example.com"
                      className={`${css.searchInput} inputStyle ${errors.companyDomain && touched.companyDomain ? 'border-danger' : ''}`}
                    />
                    {errors.companyDomain && touched.companyDomain && (
                      <ErrorMessage message={errors.companyDomain} />
                    )}
                  </div>
                </div>

                <div className="col-md-6">
                  {/* Sender Email */}
                  <div className="mb-3">
                    <label htmlFor="senderEmail" className="d-flex justify-start my-1 ms-3">
                      Sender Email <span className="text-danger ms-1">*</span>
                    </label>
                    <Field
                      type="email"
                      name="senderEmail"
                      placeholder="sender@example.com"
                      className={`${css.searchInput} inputStyle ${errors.senderEmail && touched.senderEmail ? 'border-danger' : ''}`}
                    />
                    {errors.senderEmail && touched.senderEmail && (
                      <ErrorMessage message={errors.senderEmail} />
                    )}
                  </div>

                  {/* Okta Domain */}
                  <div className="mb-3">
                    <label htmlFor="oktaDomain" className="d-flex justify-start my-1 ms-3">
                      Okta Domain
                    </label>
                    <Field
                      type='text'
                      name="oktaDomain"
                      placeholder="https://your-domain.okta.com"
                      className={`${css.searchInput} inputStyle ${errors.oktaDomain && touched.oktaDomain ? 'border-danger' : ''}`}
                    />
                    {errors.oktaDomain && touched.oktaDomain && (
                      <ErrorMessage message={errors.oktaDomain} />
                    )}
                  </div>

                  {/* Okta Client ID */}
                  <div className="mb-3">
                    <label htmlFor="oktaClientID" className="d-flex justify-start my-1 ms-3">
                      Okta Client ID
                    </label>
                    <div className="position-relative">
                      <Field
                        type={passwordVisibility.oktaClientID ? 'text' : 'password'}
                        name="oktaClientID"
                        placeholder="Okta Client ID"
                        className={`${css.searchInput} inputStyle ${errors.oktaClientID && touched.oktaClientID ? 'border-danger' : ''}`}
                        style={{ paddingRight: '40px' }}
                      />
                      <div
                        className="position-absolute end-0 pe-3"
                        style={{
                          cursor: 'pointer',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          zIndex: 10
                        }}
                        onClick={() => toggleVisibility('oktaClientID')}
                      >
                        <i className={`fa-solid ${passwordVisibility.oktaClientID ? 'fa-eye-slash' : 'fa-eye'}`} />
                      </div>
                    </div>
                    {errors.oktaClientID && touched.oktaClientID && (
                      <ErrorMessage message={errors.oktaClientID} />
                    )}
                  </div>

                  {/* File Upload - WEBP Only */}
                  <div className="mb-3">
                    <label htmlFor="file" className="d-flex justify-start my-1 ms-3">
                      File Upload (WEBP only) <span className="text-danger ms-1">*</span>
                    </label>
                    <input
                      type='file'
                      name="file"
                      accept=".webp,image/webp"
                      onChange={handleFileChange}
                      className={`${css.searchInput} inputStyle ${fileError ? 'border-danger' : ''}`}
                    />
                    {selectedFile && !fileError && (
                      <div className="text-success text-start ms-1 mt-1" style={{ fontSize: '0.875rem' }}>
                        <i className="fa-solid fa-check-circle me-1"></i>
                        Selected: {selectedFile.name}
                      </div>
                    )}
                    {fileError && (
                      <ErrorMessage message={fileError} />
                    )}
                  </div>

                  {/* Status */}
                  <div className="mb-3">
                    <label htmlFor="status" className="d-flex justify-start my-1 ms-3">
                      Status <span className="text-danger ms-1">*</span>
                    </label>
                    <Field
                      as="select"
                      name="status"
                      className={`${css.searchInput} inputStyle ${errors.status && touched.status ? 'border-danger' : ''}`}
                    >
                      <option value="">Select Status</option>
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </Field>
                    {errors.status && touched.status && (
                      <ErrorMessage message={errors.status} />
                    )}
                  </div>
                </div>
              </div>

              {/* Upload Progress */}
              {uploadStage && (
                <div className="mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <small className="text-muted">{uploadStage}</small>
                    <small className="text-muted fw-bold">{uploadProgress}%</small>
                  </div>
                  <div className="progress" style={{ height: '8px' }}>
                    <div
                      className="progress-bar progress-bar-striped progress-bar-animated bg-success"
                      role="progressbar"
                      style={{ width: `${uploadProgress}%` }}
                      aria-valuenow={uploadProgress}
                      aria-valuemin="0"
                      aria-valuemax="100"
                    ></div>
                  </div>
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                className={`${css.addButton} mb-1 w-100 d-flex justify-content-center align-items-center`}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Creating Client...' : 'Create New Client'}
                {isSubmitting && <i className="ms-3 fas fa-spinner fa-pulse"></i>}
              </button>

              {/* General Error Message */}
              {error && (
                <div className="alert alert-danger mt-3 mb-0 d-flex align-items-center" role="alert">
                  <i className="fa-solid fa-triangle-exclamation me-2"></i>
                  <span>{error}</span>
                </div>
              )}
            </Form>
          )}
        </Formik>
      </div>
    </section>
  );
}